/**
 * FreeJoinEntrySheet — atraer a registro Clerk gratis.
 *
 * Flujo:
 *  1. welcome — por qué registrarse free
 *  2. account — SignUp / SignIn Clerk embebido
 *  3. → /portal/completar-perfil?access_request=1&plan_tier=free&free_join=1
 *  4. (fuera) perfil completo → /portal/acceso-gratis con link del grupo
 *
 * El link de Telegram NO se muestra acá.
 */

import { SignIn, SignUp, useUser } from "@clerk/clerk-react";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import {
  freeJoinEntryCopy,
  type FreeJoinEntryCopy,
} from "@/lib/freeJoinEntryCopy";
import {
  buildFreeJoinCompleteProfileUrl,
  buildFreeJoinReadyUrl,
  buildFreeJoinRegistroUrl,
  persistFreeJoinContext,
} from "@/lib/freeJoinFlow";
import { isProfileComplete } from "@/lib/auth";
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { cn } from "@/lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Check, Loader2, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const CLERK_OK = Boolean(getClerkPublishableKey());

export type FreeJoinEntrySheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** URL del canal free — se guarda y se revela SOLO post-perfil. */
  destinationUrl: string;
  country?: string | null;
  language?: string | null;
  personName?: string;
  avatarUrl?: string;
  drawerContentClassName?: string;
  onStepChange?: (step: FreeJoinStep) => void;
};

export type FreeJoinStep = "welcome" | "account" | "redirecting";

function StepDots({ step }: { step: FreeJoinStep }) {
  const steps: FreeJoinStep[] = ["welcome", "account", "redirecting"];
  const idx = Math.max(0, steps.indexOf(step));
  return (
    <div className="flex items-center justify-center gap-1.5" aria-hidden>
      {steps.map((s, i) => (
        <span
          key={s}
          className={cn(
            "h-1 rounded-full transition-all duration-300",
            i === idx ? "w-5 bg-amber-400/90" : "w-1.5 bg-white/15",
            i < idx && "bg-amber-400/40",
          )}
        />
      ))}
    </div>
  );
}

function BenefitRow({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2.5 text-[13px] leading-snug text-white/70">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/25">
        <Check className="h-3 w-3 text-emerald-300" strokeWidth={2.5} aria-hidden />
      </span>
      <span>{text}</span>
    </li>
  );
}

type ClerkUserSlice = {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: ReturnType<typeof useUser>["user"] | null;
};

/** Always-hook wrapper — only mounted when ClerkProvider is present. */
function FreeJoinInnerWithClerk(props: FreeJoinEntrySheetProps) {
  const { isLoaded, isSignedIn, user } = useUser();
  return (
    <FreeJoinInnerBody
      {...props}
      clerk={{ isLoaded, isSignedIn, user: user ?? null }}
    />
  );
}

function FreeJoinInner(props: FreeJoinEntrySheetProps) {
  // Split so useUser is never called outside ClerkProvider (and never
  // conditionally — satisfies react-hooks/rules-of-hooks for CI lint).
  if (!CLERK_OK) {
    return (
      <FreeJoinInnerBody
        {...props}
        clerk={{ isLoaded: true, isSignedIn: false, user: null }}
      />
    );
  }
  return <FreeJoinInnerWithClerk {...props} />;
}

function FreeJoinInnerBody({
  open,
  destinationUrl,
  country,
  language,
  personName = BRAND_PERSON_NAME,
  avatarUrl = HEADER_PROFILE_IMAGE_URL,
  onOpenChange,
  onStepChange,
  clerk,
}: FreeJoinEntrySheetProps & { clerk: ClerkUserSlice }) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = clerk;

  const copy: FreeJoinEntryCopy = useMemo(
    () => freeJoinEntryCopy(country, language),
    [country, language],
  );

  const [step, setStep] = useState<FreeJoinStep>("welcome");
  const [authMode, setAuthMode] = useState<"sign_up" | "sign_in">("sign_up");
  const openedTracked = useRef(false);
  const redirectedRef = useRef(false);

  // Persist destination when sheet opens
  useEffect(() => {
    if (!open) {
      openedTracked.current = false;
      redirectedRef.current = false;
      return;
    }
    persistFreeJoinContext({ destinationUrl, country, language });
    setStep("welcome");
    setAuthMode("sign_up");
    if (!openedTracked.current) {
      openedTracked.current = true;
      onStepChange?.("welcome");
    }
  }, [open, destinationUrl, country, language, onStepChange]);

  const profileUrl = useMemo(
    () => buildFreeJoinCompleteProfileUrl({ language, country }),
    [language, country],
  );
  const readyUrl = useMemo(
    () => buildFreeJoinReadyUrl({ language, country }),
    [language, country],
  );

  const goAfterAuth = useCallback(() => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    persistFreeJoinContext({ destinationUrl, country, language });
    setStep("redirecting");
    onStepChange?.("redirecting");
    onOpenChange(false);

    const profileComplete = user ? isProfileComplete(user) : false;
    // Absolute URL for Clerk forceRedirect when needed
    const next = profileComplete ? readyUrl : profileUrl;
    navigate(next, { replace: false });
  }, [
    destinationUrl,
    country,
    language,
    user,
    readyUrl,
    profileUrl,
    navigate,
    onOpenChange,
    onStepChange,
  ]);

  // Tras sign-up / sign-in → perfil (o ready si ya tiene perfil)
  useEffect(() => {
    if (!open) return;
    if (step !== "account") return;
    if (!isLoaded || !isSignedIn) return;
    goAfterAuth();
  }, [open, step, isLoaded, isSignedIn, goAfterAuth]);

  const goContinueFromWelcome = () => {
    persistFreeJoinContext({ destinationUrl, country, language });
    if (!CLERK_OK) {
      // Sin Clerk embed: full-page registro free (misma query del embudo)
      navigate(buildFreeJoinRegistroUrl({ language, country }));
      onOpenChange(false);
      return;
    }
    if (isLoaded && isSignedIn) {
      goAfterAuth();
      return;
    }
    setStep("account");
    onStepChange?.("account");
  };

  /** Clerk post-auth: siempre a completar perfil (si ya completo, esa page redirige a ready). */
  const clerkAfterAuthUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${profileUrl}`
      : profileUrl;

  const stepNum = step === "welcome" ? 1 : step === "account" ? 2 : 3;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative px-5 pb-3 pt-1">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative shrink-0">
              <img
                src={avatarUrl}
                alt=""
                className="h-12 w-12 rounded-2xl object-cover shadow-lg shadow-black/40 ring-2 ring-white/15"
                width={48}
                height={48}
                decoding="async"
              />
              <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-[#0c0c0e]">
                <Sparkles className="h-2.5 w-2.5 text-white" strokeWidth={2.5} aria-hidden />
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-300/80">
                {copy.welcomeEyebrow}
              </p>
              <DrawerTitle className="truncate text-base font-semibold tracking-tight text-white/95 sm:text-lg">
                {personName}
              </DrawerTitle>
              <DrawerDescription className="sr-only">{copy.welcomeBody}</DrawerDescription>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full p-2 text-white/35 transition-colors hover:bg-white/[0.06] hover:text-white/70"
            aria-label={copy.closeLabel}
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-3">
          <StepDots step={step} />
          <span className="text-[10px] tabular-nums text-white/25">
            {copy.stepOf(stepNum, 3)}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        {step === "welcome" && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="space-y-2">
              <h2 className="text-pretty text-[1.35rem] font-semibold leading-snug tracking-tight text-white/95 sm:text-2xl">
                {copy.welcomeTitle(personName)}
              </h2>
              <p className="text-sm leading-relaxed text-white/45">{copy.welcomeBody}</p>
            </div>

            <ul className="space-y-2.5 rounded-2xl border border-white/[0.07] bg-white/[0.03] px-4 py-3.5">
              <BenefitRow text={copy.benefit1} />
              <BenefitRow text={copy.benefit2} />
              <BenefitRow text={copy.benefit3} />
            </ul>

            <p className="rounded-xl border border-white/[0.06] bg-black/25 px-3 py-2 text-[11px] leading-relaxed text-white/35">
              {copy.notVipNote}
            </p>

            <button
              type="button"
              onClick={goContinueFromWelcome}
              className={cn(
                "group flex w-full items-center justify-center gap-2 rounded-2xl",
                "bg-white text-[15px] font-semibold tracking-tight text-black",
                "px-5 py-3.5 shadow-[0_8px_28px_rgba(255,255,255,0.12)]",
                "transition-transform active:scale-[0.98]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60",
              )}
              style={{ minHeight: 52, touchAction: "manipulation" }}
            >
              <span>{copy.continueCta}</span>
              <span
                className="text-black/40 transition-transform duration-150 group-hover:translate-x-0.5"
                aria-hidden
              >
                →
              </span>
            </button>
          </div>
        )}

        {step === "account" && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="space-y-1.5">
              <h2 className="text-lg font-semibold tracking-tight text-white/95">
                {copy.accountTitle}
              </h2>
              <p className="text-sm leading-relaxed text-white/45">{copy.accountBody}</p>
              <p className="text-[11px] text-white/30">{copy.accountPrivacy}</p>
              <p className="text-[11px] font-medium text-amber-200/70">{copy.nextProfileHint}</p>
            </div>

            {CLERK_OK ? (
              <div className="space-y-3">
                <div className="flex rounded-xl border border-white/[0.08] bg-white/[0.03] p-1">
                  <button
                    type="button"
                    onClick={() => setAuthMode("sign_up")}
                    className={cn(
                      "flex-1 rounded-lg py-2 text-xs font-semibold transition-colors",
                      authMode === "sign_up"
                        ? "bg-white text-black"
                        : "text-white/45 hover:text-white/70",
                    )}
                  >
                    {copy.createAccessTab}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAuthMode("sign_in")}
                    className={cn(
                      "flex-1 rounded-lg py-2 text-xs font-semibold transition-colors",
                      authMode === "sign_in"
                        ? "bg-white text-black"
                        : "text-white/45 hover:text-white/70",
                    )}
                  >
                    {copy.haveAccountTab}
                  </button>
                </div>
                <div className="free-join-clerk overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-950/80 px-1 py-2">
                  {authMode === "sign_up" ? (
                    <SignUp
                      routing="virtual"
                      forceRedirectUrl={clerkAfterAuthUrl}
                      fallbackRedirectUrl={clerkAfterAuthUrl}
                      signInForceRedirectUrl={clerkAfterAuthUrl}
                      appearance={{
                        elements: {
                          rootBox: "w-full mx-auto",
                          card: "bg-transparent shadow-none w-full",
                          headerTitle: "hidden",
                          headerSubtitle: "hidden",
                          footer: "hidden",
                          socialButtonsBlockButton:
                            "bg-white/[0.06] border-white/10 text-white/85 hover:bg-white/[0.1]",
                          formButtonPrimary:
                            "bg-white text-black hover:bg-white/90 font-semibold",
                          formFieldInput:
                            "bg-white/[0.06] border-white/10 text-white",
                          formFieldLabel: "text-white/50",
                          dividerLine: "bg-white/10",
                          dividerText: "text-white/35",
                        },
                      }}
                    />
                  ) : (
                    <SignIn
                      routing="virtual"
                      forceRedirectUrl={clerkAfterAuthUrl}
                      fallbackRedirectUrl={clerkAfterAuthUrl}
                      signUpForceRedirectUrl={clerkAfterAuthUrl}
                      appearance={{
                        elements: {
                          rootBox: "w-full mx-auto",
                          card: "bg-transparent shadow-none w-full",
                          headerTitle: "hidden",
                          headerSubtitle: "hidden",
                          footer: "hidden",
                          socialButtonsBlockButton:
                            "bg-white/[0.06] border-white/10 text-white/85 hover:bg-white/[0.1]",
                          formButtonPrimary:
                            "bg-white text-black hover:bg-white/90 font-semibold",
                          formFieldInput:
                            "bg-white/[0.06] border-white/10 text-white",
                          formFieldLabel: "text-white/50",
                          dividerLine: "bg-white/10",
                          dividerText: "text-white/35",
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={goContinueFromWelcome}
                className="w-full rounded-2xl bg-white py-3.5 text-sm font-semibold text-black"
              >
                {copy.continueCta}
              </button>
            )}
          </div>
        )}

        {step === "redirecting" && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-amber-300/80" />
            <p className="text-sm text-white/50">{copy.nextProfileHint}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FreeJoinEntrySheet(props: FreeJoinEntrySheetProps) {
  const { open, onOpenChange, drawerContentClassName } = props;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent
        className={cn(
          "mx-auto flex max-h-[min(92dvh,720px)] flex-col border-white/[0.08] bg-[#0c0c0e] text-white",
          "rounded-t-[22px] pb-0",
          "md:max-w-[430px]",
          drawerContentClassName,
        )}
      >
        {open ? <FreeJoinInner {...props} /> : null}
      </DrawerContent>
    </Drawer>
  );
}
